// src/components/manager/ManagerRoutesPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
    Container, Card, CardBody, CardTitle, Row, Col,
    Table, Alert, Spinner, Input, Button, Modal, ModalHeader, ModalBody, FormGroup, Label
} from "reactstrap";
import AuthFunctions from '../../services/AuthService';
import Session from '../../auth/Session';
import { useNavigate } from 'react-router-dom';
import * as dayjs from 'dayjs';
import SearchableDropdownFilter from "../SearchableDropdownFilter";
import { getFilters, saveFilter } from "../filterStorage";

const getCustomerLabel = (customer) => customer.name;
const getSubCustomerLabel = (subCustomer) =>
    `${subCustomer.name}${subCustomer.customer ? ` — ${subCustomer.customer.name}` : ""}`;
const getOptionId = (option) => option.id;

const ManagerRoutes = () => {
    const navigate = useNavigate();
    const apiUrl = "/api/manager/weekly-route-schedule";
    const driversApi = "/api/drivers/list";
    const subCustomersApi = "/api/subcustomers";
    const customersApi = "/api/customers";
    const routeWeeksApi = "/api/routes/weeks";

    const [loading, setLoading] = useState(true);
    const [isAdmin, setIsAdmin] = useState(false);
    const [schedule, setSchedule] = useState(null);
    const [routeSchedules, setRouteSchedules] = useState([]);
    const [filteredRoutes, setFilteredRoutes] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [subCustomers, setSubCustomers] = useState([]);
    const [error, setError] = useState("");
    const [customerId, setCustomerId] = useState(() => getFilters().customerId || "");
    const [subCustomerId, setSubCustomerId] = useState(() => getFilters().subCustomerId || "");

    const [weeks, setWeeks] = useState([
        { endingDate: '', displayDate: '' }
    ]);
    const [selectedWeek, setSelectedWeek] = useState('');

    const getAssignedSubCustomers = (routes) => {
        const map = new Map();
        for (const rs of routes || []) {
            const subCustomer = rs?.route?.subCustomer;
            if (subCustomer?.id && !map.has(subCustomer.id)) {
                map.set(subCustomer.id, subCustomer);
            }
        }

        return Array.from(map.values()).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    };

    const buildRouteUrl = (endingDate, selectedCustomerId = customerId, selectedSubCustomerId = subCustomerId) => {
        const params = new URLSearchParams();
        if (endingDate) params.set('endingDate', endingDate);
        if (isAdmin) {
            params.set('customerId', selectedCustomerId);
            params.set('subCustomerId', selectedSubCustomerId);
        }
        const query = params.toString();
        return query ? `${apiUrl}?${query}` : apiUrl;
    };

    const clearRouteData = () => {
        setSchedule(null);
        setRouteSchedules([]);
        setFilteredRoutes([]);
    };

    const loadData = async () => {
        setLoading(true);
        setError("");
        try {
            const isAuthenticated = await AuthFunctions.getAuthenticatedAsync();
            if (!isAuthenticated) {
                navigate('/logout');
            }
            else {
                const token = await Session.getToken();
                const adminUser = await AuthFunctions.getIsAdminAsync();
                setIsAdmin(adminUser);

                const commonRequests = [
                    fetch(driversApi, { headers: { Authorization: `Bearer ${token}` } }),
                    fetch(routeWeeksApi, { headers: { Authorization: `Bearer ${token}` } })
                ];

                if (adminUser) {
                    commonRequests.push(fetch(customersApi, { headers: { Authorization: `Bearer ${token}` } }));
                    commonRequests.push(fetch(subCustomersApi, { headers: { Authorization: `Bearer ${token}` } }));
                } else {
                    commonRequests.push(fetch(apiUrl, { headers: { Authorization: `Bearer ${token}` } }));
                }

                const responses = await Promise.all(commonRequests);
                const [dRes, rwRes] = responses;
                setDrivers(dRes.ok ? await dRes.json() : []);
                setWeeks(rwRes.ok ? await rwRes.json() : []);

                if (adminUser) {
                    const cRes = responses[2];
                    const scRes = responses[3];
                    setCustomers(cRes.ok ? await cRes.json() : []);
                    setSubCustomers(scRes.ok ? await scRes.json() : []);
                    clearRouteData();
                } else {
                    const res = responses[2];
                    if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
                    const data = await res.json();
                    const routes = data.routeSchedules || [];
                    setSchedule(data.schedule);
                    setRouteSchedules(routes);
                    setSubCustomers(getAssignedSubCustomers(routes));
                }
            }
        } catch (e) {
            setError(e.message || "Failed to load manager data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const stored = getFilters();
        const match = stored.selectedWeek ? weeks.find(w => w.endingDate === stored.selectedWeek) : null;
        const selected = match || weeks.find(w => w.selected === true);
        if (!selected) return;
        setSelectedWeek(selected.endingDate);
        if (isAdmin && stored.customerId && stored.subCustomerId) {
            fetchScheduleForDate(selected.endingDate, stored.customerId, stored.subCustomerId);
        }
    }, [weeks]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { loadData(); }, []);

    const fmt = (d) => (d ? new Date(d).toLocaleDateString() : "");

    // Group by (scheduleId, routeId)
    const grouped = useMemo(() => {
        const m = new Map();
        for (const rs of filteredRoutes) {
            const key = `${rs.scheduleId}|${rs.routeId}`;
            if (!m.has(key)) m.set(key, []);
            m.get(key).push(rs);
        }
        return Array.from(m.values()).map(list => {
            list.sort((a, b) => a.id - b.id);
            return { parent: list[0], children: list.slice(1) };
        });
    }, [filteredRoutes]);

    const availableSubCustomers = useMemo(() => {
        if (!isAdmin || customerId === '') return subCustomers;
        return subCustomers.filter(sc => `${sc.customerId}` === `${customerId}`);
    }, [isAdmin, subCustomers, customerId]);

    useEffect(() => {
        let filtered = [];
        if (isAdmin && subCustomerId === '') {
            filtered = [];
        } else if (subCustomerId !== '') {
            filtered = routeSchedules.filter(rs => `${rs?.route?.subCustomerId}` === subCustomerId);
        } else {
            filtered = [...routeSchedules];
        }
        setFilteredRoutes(filtered);
    }, [routeSchedules, subCustomerId, isAdmin]);

    const fetchScheduleForDate = async (endingDate, selectedCustomerId = customerId, selectedSubCustomerId = subCustomerId) => {
        if (isAdmin && (!selectedCustomerId || !selectedSubCustomerId)) {
            clearRouteData();
            return;
        }

        const token = await Session.getToken();
        const res = await fetch(buildRouteUrl(endingDate, selectedCustomerId, selectedSubCustomerId), { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
            let data = await res.json();
            setSchedule(data.schedule);
            setRouteSchedules(data.routeSchedules || []);
        }
    }

    const sumDays = (parent, children) => {
        const toNum = (v) => (v === "" || v === null || v === undefined ? 0 : Number(v));
        return [parent, ...children].reduce((acc, rs) => acc + toNum(rs.daysOnRoute), 0);
    };

    const handleShowDays = (rs, updated) => {
        setRouteSchedules(prev => prev.map(p => ((p.routeId === rs.routeId && p.driverId === rs.driverId) ? { ...p, showDays: updated } : p)));
    };

    return (
        <Container className="mt-0">
            <Card>
                <CardBody>
                    <Row className="mb-3">
                        <Col><h5>Review Weekly Routes</h5></Col>
                    </Row>
                    <Row className="mb-3">
                        <Col md={4}>
                            <Input
                                type="select"
                                value={selectedWeek}
                                onChange={(e) => {
                                    let value = e.target.value;
                                    setSelectedWeek(value);
                                    saveFilter("selectedWeek", value);
                                    fetchScheduleForDate(value);
                                }}
                            >
                                <option value="">-- Select Week --</option>
                                {weeks.map(w => (
                                    <option key={w.endingDate} value={w.endingDate}>
                                        {w.displayDate}
                                    </option>
                                ))}
                            </Input>
                        </Col>
                        {isAdmin && (
                            <Col md={4}>
                                <SearchableDropdownFilter
                                    value={customerId}
                                    options={customers}
                                    placeholder="Select Customer..."
                                    getOptionValue={getOptionId}
                                    getOptionLabel={getCustomerLabel}
                                    onChange={(value) => {
                                        setCustomerId(value);
                                        setSubCustomerId("");
                                        saveFilter("customerId", value);
                                        saveFilter("subCustomerId", "");
                                        clearRouteData();
                                    }}
                                />
                            </Col>
                        )}
                        <Col md={4}>
                            <SearchableDropdownFilter
                                value={subCustomerId}
                                options={availableSubCustomers}
                                placeholder="Select Sub-Customer..."
                                getOptionValue={getOptionId}
                                getOptionLabel={getSubCustomerLabel}
                                onChange={(value) => {
                                    setSubCustomerId(value);
                                    saveFilter("subCustomerId", value);
                                    if (isAdmin) fetchScheduleForDate(selectedWeek, customerId, value);
                                }}
                                disabled={isAdmin && !customerId}
                            />
                        </Col>
                    </Row>
                    {loading ? (
                        <div className="py-5 text-center"><Spinner /></div>
                    ) : error ? (
                        <Alert color="danger">{error}</Alert>
                    ) : isAdmin && (!customerId || !subCustomerId) ? (
                        <Alert color="info">Select a customer and subcustomer to load weekly billing routes.</Alert>
                    ) : (
                        <>
                            {/* Schedule summary */}
                            {schedule && (
                                <Row className="mb-4">
                                    <Col md={4}>
                                        <Card>
                                            <CardBody>
                                                <div className="text-muted">Payment Week</div>
                                                <div className="h5 mb-0">{fmt(schedule.startDate)} - {fmt(schedule.endDate)}</div>
                                            </CardBody>
                                        </Card>
                                    </Col>
                                    <Col md={4}>
                                        <Card>
                                            <CardBody>
                                                <div className="text-muted">Check Date</div>
                                                <div className="h5 mb-0">{fmt(schedule.payDate)}</div>
                                            </CardBody>
                                        </Card>
                                    </Col>
                                </Row>
                            )}

                            {/* RouteSchedules table */}
                            <Table bordered hover responsive>
                                <thead className="table-light">
                                    <tr>
                                        <th></th>
                                        <th>Sub-Customer</th>
                                        <th>Route</th>
                                        <th>Driver</th>
                                        <th style={{ minWidth: 140 }}>Days On Route</th>
                                        <th>Total Days</th>
                                        <th>Expected Days</th>
                                        {/*    <th style={{ width: 60 }}></th>*/}
                                    </tr>
                                </thead>
                                <tbody>
                                    {grouped.length === 0 ? (
                                        <tr><td colSpan="7" className="text-center">No route schedules</td></tr>
                                    ) : (
                                        grouped.flatMap(({ parent, children }) => {
                                            const expected = parent?.route?.quantity ?? 0;
                                            const total = sumDays(parent, children);
                                            const mismatch = Number(total) !== Number(expected);
                                            const makeRow = (rs, isParent) => {
                                                const displayVal = typeof rs.daysOnRoute === "number" ? rs.daysOnRoute : (rs.daysOnRoute || "");
                                                const prevShowDays = rs?.showDays || false;

                                                return (
                                                    <>
                                                        <tr key={`${rs.routeId}-${rs.scheduleId}-${rs.driverId ? rs.driverId : 0}`}>
                                                            <td className="text-end">
                                                                {rs.dailySchedules && rs.dailySchedules.length > 0 && (
                                                                    <Button size="sm" color="secondary" onClick={() => { handleShowDays(rs, !prevShowDays) }}>{rs?.showDays ? '-' : '+'}</Button>
                                                                )}
                                                            </td>
                                                            <td className={!isParent ? 'bg-light' : ''}>{isParent && (rs.route?.subCustomer?.name ?? "")}</td>
                                                            <td className={!isParent ? 'bg-light' : ''}>{isParent && (rs.route?.name ?? "")}</td>
                                                            <td>
                                                                {rs.driverId ? drivers.filter(d => d.id === rs.driverId).map(d => (
                                                                    <>
                                                                        {d.driverName}{d.businessName ? ` — ${d.businessName}` : ""}
                                                                    </>
                                                                )) : (
                                                                    <>
                                                                        {'No Driver Selected'}
                                                                    </>
                                                                )}
                                                            </td>
                                                            <td>
                                                                <Input
                                                                    type="number"
                                                                    min="0"
                                                                    step="0.01"
                                                                    value={displayVal}
                                                                    disabled
                                                                />
                                                            </td>
                                                            <td className={isParent && mismatch ? "table-danger fw-bold" : (isParent ? "fw-bold" : "")}>
                                                                {isParent ? total : ""}
                                                            </td>
                                                            <td>{expected}</td>
                                                        </tr>
                                                        {rs.showDays && rs.dailySchedules.map(d => (
                                                            <tr key={`${d.driverId}-${fmt(d.routeDate)}`}>
                                                                <td className={'bg-light'}></td>
                                                                <td className={'bg-light'}></td>
                                                                <td className={'bg-light'}></td>
                                                                <td className={'bg-info'}><strong>Date Entered:</strong> {fmt(d.routeDate)}</td>
                                                                <td>
                                                                    <Input
                                                                        type="number"
                                                                        min="0"
                                                                        step="0.01"
                                                                        value={d.daysOnRoute}
                                                                        disabled
                                                                    />
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </>
                                                );
                                            };
                                            return [makeRow(parent, true), ...children.map(c => makeRow(c, false))];
                                        })
                                    )}
                                </tbody>
                            </Table>
                        </>
                    )}
                </CardBody>
            </Card>

            {/* Add Child Modal */}
            {/*<Modal isOpen={addOpen} toggle={() => setAddOpen(false)}>*/}
            {/*    <ModalHeader toggle={() => setAddOpen(false)}>Add Driver to Route</ModalHeader>*/}
            {/*    <ModalBody>*/}
            {/*        <FormGroup>*/}
            {/*            <Label for="driverSel">Driver</Label>*/}
            {/*            <Input id="driverSel" type="select" value={newDriverId} onChange={(e) => setNewDriverId(e.target.value)}>*/}
            {/*                <option value="">-- Select Driver --</option>*/}
            {/*                {drivers.map(d => (*/}
            {/*                    <option key={d.id} value={d.id}>*/}
            {/*                        {d.driverName}{d.businessName ? ` — ${d.businessName}` : ""}*/}
            {/*                    </option>*/}
            {/*                ))}*/}
            {/*            </Input>*/}
            {/*        </FormGroup>*/}
            {/*        <FormGroup>*/}
            {/*            <Label for="daysInput">Days On Route</Label>*/}
            {/*            <Input id="daysInput" type="number" min="0" step="0.5" value={newDays} onChange={(e) => setNewDays(e.target.value)} />*/}
            {/*        </FormGroup>*/}
            {/*        <div className="d-flex justify-content-end gap-2">*/}
            {/*            <Button color="secondary" onClick={() => setAddOpen(false)}>Cancel</Button>*/}
            {/*            <Button color="primary" onClick={submitAddChild} disabled={!newDriverId}>Add</Button>*/}
            {/*        </div>*/}
            {/*    </ModalBody>*/}
            {/*</Modal>*/}
        </Container>
    );
};

export default ManagerRoutes;
